import { t } from "i18next";

import Chevron from "@/assets/icons/chevron.svg?react";
import level1 from "@/assets/images/level-1.png";
import level2 from "@/assets/images/level-2.png";
import level3 from "@/assets/images/level-3.png";
import level4 from "@/assets/images/level-4.png";
import { Button } from "@/components/Button";
import { ButtonType } from "@/components/Button/types";
import { LevelSuccess } from "@/components/Levels/components/LevelSuccess";
import { LevelSuccessContainer } from "@/components/Levels/components/LevelSuccess/styles";
import { getAvailableLevels } from "@/constants/levels";
import { LevelProps } from "@/interfaces/components";
import { Network } from "@/interfaces/network";
import { ButtonActionMap } from "@/services/config/translation";
import { colors } from "@/theme/colors";

export const getLevels = ({
  mint,
  mintNFT,
  userLevel,
  userScore,
  openLevelModal,
  network,
  news,
  socials,
}: {
  mint: { handler: () => void; loading: boolean; disabled: boolean };
  mintNFT: {
    handler: () => void;
    loading: boolean;
    isSoldOut: boolean;
    isPurchased: boolean;
    minAllowedScore: number;
  };
  userLevel: number;
  userScore: number;
  openLevelModal: (level: LevelProps) => void;
  network: Network;
  news: {
    url: string | null;
    action: () => void;
  };
  socials: {
    x: boolean;
    telegram: boolean;
  };
}): Array<LevelProps> => {
  const availableLevels = getAvailableLevels(network);

  return [
    {
      level: 1,
      get title() {
        return this.soon ? t("Levels.ComingSoon") : t("Levels.1.Title", { network });
      },
      get completed() {
        return userLevel >= this.level;
      },
      get actions() {
        if (this.soon) return null;
        return (
          <Button
            key="MintNow"
            name="MintNow"
            type="primary"
            handler={mint.handler}
            disabled={mint.disabled}
            loading={mint.loading}
          />
        );
      },
      get soon() {
        return !availableLevels[this.level - 1];
      },
      img: level1,
      style: {
        background: colors.level1Background,
        border: colors.level1Border,
        boxShadow: colors.level1BoxShadow,
      },
    },
    {
      level: 2,
      get title() {
        return this.soon ? t("Levels.ComingSoon") : t("Levels.2.Title");
      },
      get completed() {
        return userLevel >= this.level;
      },
      get actions() {
        if (this.soon || userLevel < this.level - 1) return null;
        let title: ButtonActionMap = "MintNFT";
        let handler = mintNFT.handler;
        let disabled = false;
        let type: ButtonType = "primary";

        if (mintNFT.isSoldOut) {
          if (news.url) {
            title = "CheckNews";
            handler = news.action;
            disabled = false;
            type = "news";
          } else {
            title = "SoldOut";
            disabled = true;
          }
        } else {
          if (userScore < mintNFT.minAllowedScore) {
            title = "YourScoreIsLow";
            disabled = true;
          } else {
            title = "MintNFT";
          }
        }
        return (
          <Button
            key="MintNFT"
            name={title}
            additionalDataForName={{ allowedScore: mintNFT.minAllowedScore }}
            type={type}
            handler={handler}
            loading={mintNFT.loading}
            disabled={disabled}
          />
        );
      },
      get soon() {
        return !availableLevels[this.level - 1];
      },
      img: level2,
      style: {
        background: colors.level2Background,
        border: colors.level2Border,
        boxShadow: colors.level2BoxShadow,
      },
    },
    {
      level: 3,
      get title() {
        return this.soon ? t("Levels.ComingSoon") : t("Levels.3.Title");
      },
      get completed() {
        const { x, telegram } = socials;
        return userLevel >= this.level || (x && telegram);
      },
      get additionalCompleted() {
        const { x, telegram } = socials;
        if (!x && !telegram) return null;
        return (
          <LevelSuccessContainer>
            <LevelSuccess status={telegram ? "completed" : "pending"} title={t("Levels.3.Telegram")} />
            <LevelSuccess status={x ? "completed" : "pending"} title={t("Levels.3.X")} />
          </LevelSuccessContainer>
        );
      },
      get handler() {
        if (userLevel === this.level) return undefined;
        return () => openLevelModal({ ...this });
      },
      get actions() {
        if (this.soon || userLevel < this.level - 2) return null;
        return <Chevron />;
      },
      get soon() {
        return !availableLevels[this.level - 1];
      },
      img: level3,
      style: {
        background: colors.level3Background,
        border: colors.level3Border,
        boxShadow: colors.level3BoxShadow,
      },
    },
    {
      level: 4,
      get title() {
        return this.soon ? t("Levels.ComingSoon") : t("Levels.4.Title");
      },
      description: t("Levels.4.Description"),
      get completed() {
        return userLevel >= this.level;
      },
      get actions() {
        if (this.soon || userLevel < this.level - 1) return null;
        return (
          <Button
            key="VerifyNow"
            name="VerifyNow"
            //TODO: add handler
            handler={() => {}}
            disabled
          />
        );
      },
      get soon() {
        return !availableLevels[this.level - 1];
      },
      img: level4,
      style: {
        background: colors.level4Background,
        border: colors.level4Border,
        boxShadow: colors.level4BoxShadow,
      },
    },
  ];
};
